/**
 * loadGoogleMaps.js · v2.7.38 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * Singleton Google Maps JS API loader. Multiple components calling
 * this return the SAME in-flight promise — so the script is only
 * injected once, even if the user navigates between map-bearing
 * pages (Live Dispatch Map / Weather Radar / Public Local Weather).
 *
 * Returns a Promise<window.google.maps> that resolves once `google.maps`
 * is available on the window. Rejects if the API key is missing.
 *
 * Usage:
 *     import { loadGoogleMaps } from "@/lib/loadGoogleMaps";
 *     loadGoogleMaps().then((maps) => {
 *       new maps.Map(divRef.current, { center, zoom });
 *     });
 *
 * v2.7.38 root-cause for "Live Weather Radar shows no tiles":
 *   WeatherCommanderPage.jsx previously gated its init effect with
 *   `if (!window.google) return;` — but the effect's deps were
 *   `[mode, compact]`, so it never re-ran once the script finally
 *   loaded. Switching to this promise-based loader guarantees the
 *   init runs as soon as the API is ready.
 */

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let _loaderPromise = null;

export function loadGoogleMaps() {
  // Hot path — already loaded
  if (typeof window !== "undefined" && window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (_loaderPromise) return _loaderPromise;

  _loaderPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("loadGoogleMaps called outside browser"));
      return;
    }
    if (!MAPS_KEY) {
      reject(new Error("Missing REACT_APP_GOOGLE_MAPS_API_KEY · cannot initialize Google Maps"));
      return;
    }
    // Reuse an existing script tag if one was injected by any peer component
    const existing = document.querySelector('script[data-buller-google-maps]');
    if (existing) {
      if (window.google?.maps) {
        resolve(window.google.maps);
        return;
      }
      existing.addEventListener("load", () => resolve(window.google.maps), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=marker&v=weekly`;
    s.async = true;
    s.defer = true;
    s.setAttribute("data-buller-google-maps", "1");
    s.onload = () => resolve(window.google.maps);
    s.onerror = (e) => {
      _loaderPromise = null; // allow retry on subsequent calls
      reject(e);
    };
    document.head.appendChild(s);
  });

  return _loaderPromise;
}

export default loadGoogleMaps;
